import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Sidebar from "../partials/Sidebar";
import Header from "../partials/Header2";
import styles from "../Lockers/style.module.css";
import modalStyles from "./streaming.module.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import HashLoader from "react-spinners/ClipLoader";
import Delete from "../../assets/icons/delete.svg";
import Edit from "../../assets/icons/edit.svg";
import { toast } from "react-toastify";
const DEFAULT_ENDPOINT = "stream/storage/url";

const buildFullUrl = (stream) => {
  if (!stream?.streaming_url) return "";
  if (
    stream.endpoint_path === "rtsp" ||
    stream.streaming_url.startsWith("rtsp://") ||
    stream.streaming_url.startsWith("rtsps://")
  ) {
    return stream.streaming_url;
  }
  const base = stream.streaming_url.replace(/\/$/, "");
  const path = stream.endpoint_path?.replace(/^\//, "");
  return path ? `${base}/${path}` : base;
};

const parseStreamUrl = (fullUrl) => {
  const trimmed = fullUrl.trim();
  if (!trimmed) {
    return { streaming_url: "", endpoint_path: DEFAULT_ENDPOINT };
  }

  if (trimmed.startsWith("rtsp://") || trimmed.startsWith("rtsps://")) {
    return { streaming_url: trimmed, endpoint_path: "rtsp" };
  }

  try {
    const parsed = new URL(trimmed);
    const path = `${parsed.pathname}${parsed.search}`.replace(/^\//, "");

    if (path.startsWith("stream/")) {
      return {
        streaming_url: `${parsed.protocol}//${parsed.host}`,
        endpoint_path: path,
      };
    }

    return {
      streaming_url: trimmed.replace(/\/$/, ""),
      endpoint_path: DEFAULT_ENDPOINT,
    };
  } catch (error) {
    return {
      streaming_url: trimmed.replace(/\/$/, ""),
      endpoint_path: DEFAULT_ENDPOINT,
    };
  }
};

const StreamingList = () => {
  const url = process.env.REACT_APP_APP_BACK_URL;
  const [data, setData] = useState([]);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisplay, setIsDisplay] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: "", url: "" });
  const [isActive, setIsActive] = useState(false);

  const authHeaders = () => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Access-Control-Allow-Origin", "*");
    headers.append("Access-Control-Allow-Credentials", "true");
    headers.append("auth-token", localStorage.getItem("token"));
    return headers;
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setIsDisplay(false);
      setIsNotFound(false);

      const response = await fetch(`${url}/api/streaming/list`, {
        mode: "cors",
        method: "GET",
        headers: authHeaders(),
      });
      setIsLoading(false);

      const json = await response.json();
      if (json.success && json.streams.length > 0) {
        setData(json.streams);
        setIsDisplay(true);
      } else {
        setData([]);
        setIsNotFound(true);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({ name: "", url: "" });
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleInput = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const { streaming_url, endpoint_path } = parseStreamUrl(formData.url);
    const payload = {
      name: formData.name,
      streaming_url,
      endpoint_path,
    };

    const endpoint = editingId
      ? `${url}/api/streaming/update/${editingId}`
      : `${url}/api/streaming/add`;
    const method = editingId ? "PUT" : "POST";

    try {
      const response = await fetch(endpoint, {
        mode: "cors",
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      setIsSaving(false);

      if (json.success) {
        toast.success(
          editingId
            ? "Streaming updated successfully"
            : "Streaming added successfully"
        );
        closeModal();
        fetchData();
        window.dispatchEvent(new Event("streaming-updated"));
      } else {
        toast.error(json.error || "Something went wrong");
      }
    } catch (error) {
      setIsSaving(false);
      toast.error(error.message);
    }
  };

  const handleActivate = async (id, name) => {
    const result = await Swal.fire({
      title: "Set as active?",
      text: `Only one stream can be active. "${name}" will become the active stream.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, activate",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${url}/api/streaming/activate/${id}`, {
        mode: "cors",
        method: "PUT",
        headers: authHeaders(),
      });
      const json = await response.json();
      if (json.success) {
        toast.success("Active streaming updated");
        fetchData();
        window.dispatchEvent(new Event("streaming-updated"));
      } else {
        toast.error(json.error || "Failed to activate");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      name: item.name,
      url: buildFullUrl(item),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${url}/api/streaming/delete/${id}`, {
        mode: "cors",
        method: "DELETE",
        headers: authHeaders(),
      });
      const json = await response.json();
      if (json.success) {
        Swal.fire("Deleted!", "Streaming deleted successfully!", "success");
        fetchData();
        window.dispatchEvent(new Event("streaming-updated"));
      } else {
        Swal.fire("Error", json.error || "Delete failed", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const toggleActive = () => setIsActive(!isActive);

  const streamingModal =
    isModalOpen &&
    createPortal(
      <div
        className={modalStyles.overlay}
        role="presentation"
        onClick={closeModal}
      >
        <div
          className={modalStyles.content}
          role="dialog"
          aria-modal="true"
          aria-labelledby="streaming-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={modalStyles.header}>
            <h4 id="streaming-modal-title" className={modalStyles.title}>
              {editingId ? "Edit Streaming" : "Add Streaming"}
            </h4>
            <button
              type="button"
              className={modalStyles.closeBtn}
              aria-label="Close"
              onClick={closeModal}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleInput}
                placeholder="Storage Camera"
                required
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="form-label">URL</label>
              <input
                className="form-control"
                name="url"
                value={formData.url}
                onChange={handleInput}
                placeholder="rtsp://user:pass@host:3057/chID=5&streamType=main&linkType=tcp"
                required
              />
            </div>
            <div className={modalStyles.actions}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-prim w-auto"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : editingId ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );

  return (    <div className={styles.heading}>
      <section className={styles.sidebar_wrap}>
        <div className="body-wrap">
          <div className="container-fluid px-0">
            <div className="d-flex">
              <Sidebar />
              <div className="p-3 w-100 main__content">
                <Header isActive={isActive} toggleActive={toggleActive} />
                <div className="general-dashboard">
                  <div className="container-fluid">
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-lg-6 my-auto">
                          <div className="user-wrap">
                            <h3>Streaming</h3>
                            <p>List of all the streams in the system</p>
                          </div>
                        </div>
                        <div className="col-lg-6 text-end">
                          <div className="filter-right-sec">
                            <button
                              type="button"
                              className="btn btn-prim w-auto"
                              onClick={openAddModal}
                            >
                              Add New Streaming
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="table-wrap">
                      <div className="row">
                        <div className="col-12">
                          <div className={styles.card_box}>
                            <div className={styles.tables_wrap}>
                              <div className="table-responsive">
                                <table className="table align-middle">
                                  <thead>
                                    <tr>
                                      <th
                                        style={{
                                          fontWeight: "700",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Name
                                      </th>
                                      <th
                                        style={{
                                          fontWeight: "700",
                                          fontSize: "12px",
                                        }}
                                      >
                                        URL
                                      </th>
                                      <th
                                        style={{
                                          fontWeight: "700",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Status
                                      </th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {isLoading && (
                                      <tr>
                                        <td
                                          colSpan={4}
                                          className="text-center"
                                          style={{ background: "transparent" }}
                                        >
                                          <HashLoader
                                            color="#0b0d41"
                                            loading={isLoading}
                                            size={40}
                                          />
                                        </td>
                                      </tr>
                                    )}
                                    {isDisplay &&
                                      data.map((item) => (
                                        <tr key={item._id}>
                                          <td>
                                            <div className="usr_det_tb">
                                              <span>{item.name}</span>
                                            </div>
                                          </td>
                                          <td
                                            style={{
                                              maxWidth: "420px",
                                              wordBreak: "break-all",
                                            }}
                                          >
                                            {buildFullUrl(item)}
                                          </td>
                                          <td>
                                            {item.is_active ? (
                                              <span className="badge bg-success">
                                                Active
                                              </span>
                                            ) : (
                                              <span className="badge bg-secondary">
                                                Inactive
                                              </span>
                                            )}
                                          </td>
                                          <td className="d-flex justify-content-end gap-2">
                                            {!item.is_active && (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success"
                                                onClick={() =>
                                                  handleActivate(
                                                    item._id,
                                                    item.name
                                                  )
                                                }
                                              >
                                                Activate
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              className="btn btn-sm p-0 border-0 bg-transparent"
                                              onClick={() => handleEdit(item)}
                                            >
                                              <img src={Edit} alt="edit" />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm p-0 border-0 bg-transparent"
                                              onClick={() =>
                                                handleDelete(item._id)
                                              }
                                            >
                                              <img src={Delete} alt="delete" />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    {isNotFound && !isLoading && (
                                      <tr>
                                        <td colSpan={4} className="text-center">
                                          <p
                                            className="text-center m-0"
                                            style={{ color: "red" }}
                                          >
                                            No data found.
                                          </p>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {streamingModal}
    </div>
  );
};
export default StreamingList;
